import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LoggingService } from '../shared/services/logging/logging.service';
import {
    IGroup,
    IGroupElement,
    IGroupElementType,
    IGroupMember,
    IGroupType
} from './IGroups';
import {
    IGroupDeleteResponse,
    IGroupElementResponse,
    IGroupMemberResponse,
    IGroupResponse
} from './IGroupsResponse';

@Injectable({
    providedIn: 'root'
})
export class GroupsService {
    constructor(
        @Inject(HttpClient) private http: HttpClient,
        @Inject(LoggingService) private log: LoggingService
    ) {}

    public roles$: Observable<Array<IGroup>> = new Observable<Array<IGroup>>(
        o => (this._observer = o)
    );

    private _observer: Observer<Array<IGroup>>;
    private groupTypeUri = `${environment.apiLocation}/GroupType/CategoryID`;
    private groupElementTypeUri = `${environment.apiLocation}/GroupElementType`;
    private groupUri = `${environment.apiLocation}/Group`;
    private groupMemberUri = `${environment.apiLocation}/GroupMember`;

    // SERVICE CALLS FOR GROUP ELEMENT

    private groupElementUri = `${environment.apiLocation}/GroupElement`;

    // SERVICE CALL FOR GROUP TYPE
    public getGroupType(): Observable<Array<IGroupType>> {
        return this.http.get<Array<IGroupType>>(this.groupTypeUri).pipe(res => {
            this.log.verbose({
                type: 'GroupsService:getGroupType:reponse',
                res
            });
            return res;
        });
    }
    // SERVICE CALL FOR GROUP ELEMENT TYPE
    public postGroupElementType(params: {
        GroupID: number;
    }): Observable<Array<IGroupElementType>> {
        const request = { GroupID: (params && params.GroupID) || 0 };
        return this.http
            .post<Array<IGroupElementType>>(this.groupElementTypeUri, request)
            .pipe(res => {
                this.log.verbose({
                    type: 'GroupsService:postGroupElementType:reponse',
                    res
                });
                return res;
            });
    }

    // SERVICE CALLS FOR GROUP
    // Service call for loading groups for the page
    public postGroup(params: {
        Categoryid: string;
    }): Observable<Array<IGroup>> {
        const request = {
            Categoryid: (params && params.Categoryid) || ''
        };
        return this.http
            .post<Array<IGroup>>(this.groupUri, request)
            .pipe(
                tap(data =>
                    this.log.verbose({
                        type: 'GroupsService:postGroup:reponse',
                        data
                    })
                )
            );
    }
    // Service call for group update
    public putGroup(groups: Array<IGroup>): Observable<IGroupResponse> {
        this.log.debug({ type: 'GroupsService:putGroup', groups });
        return this.http
            .put<IGroupResponse>(this.groupUri, { GroupList: groups })
            .pipe(res => {
                this.log.verbose({
                    type: 'GroupsService:putGroup:reponse',
                    res
                });
                return res;
            });
    }

    // Service call for deleting a group
    public deleteGroup(
        groupIDs: Array<number>
    ): Observable<IGroupDeleteResponse> {
        this.log.debug({ type: 'GroupsService:deleteGroup', groupIDs });

        return (
            this.http
                // TODO: change to .delete<IResponse> when upgrading to NG7
                .request<IGroupDeleteResponse>('DELETE', this.groupUri, {
                    body: { EntityIDs: groupIDs }
                })
                .pipe(
                    tap(data =>
                        this.log.verbose({
                            type: 'GroupsService:deleteGroup:reponse',
                            data
                        })
                    )
                )
        );
    }

    // SERVICE CALLS FOR GROUP MEMBER

    // Service call for getting group members
    public postGroupMember(params: {
        GroupID: number;
        IsGroupMember?: boolean;
    }): Observable<Array<IGroupMember>> {
        const request = {
            GroupID: (params && params.GroupID) || 1,
            IsGroupMember: (params && params.IsGroupMember) || null
        };
        return this.http
            .post<Array<IGroupMember>>(this.groupMemberUri, request)
            .pipe(res => {
                this.log.verbose({
                    type: 'GroupsService:putGroup:reponse',
                    res
                });
                return res;
            });
    }

    // Service call for group member update / for a "delete" send isMember = false
    public putGroupMember(
        groupMembers: Array<IGroupMember>
    ): Observable<IGroupMemberResponse> {
        this.log.debug({ type: 'GroupsService:putGroup', groupMembers });
        this.log.verbose(JSON.stringify({ GroupMemberList: groupMembers }));
        return this.http
            .put<IGroupMemberResponse>(this.groupMemberUri, {
                GroupMemberList: groupMembers
            })
            .pipe(res => {
                this.log.verbose({
                    type: 'GroupsService:putGroup:reponse',
                    res
                });
                return res;
            });
    }

    // Service call for getting group elements
    public postGroupElement(params: {
        GroupID: number;
        GroupElementTypeID: number;
        ListID: string;
    }): Observable<Array<IGroupElement>> {
        const request = {
            GroupID: (params && params.GroupID) || 0,
            GroupElementTypeID: (params && params.GroupElementTypeID) || 0,
            ListID: (params && params.ListID) || ''
        };
        return this.http
            .post<Array<IGroupElement>>(this.groupElementUri, request)
            .pipe(res => {
                this.log.verbose({
                    type: 'GroupsService:getGroupElement:reponse',
                    res
                });
                return res;
            });
    }

    // Service call for group element update / for a "delete" send isElement = false
    public putGroupElement(
        groupElements: Array<IGroupElement>
    ): Observable<IGroupElementResponse> {
        this.log.debug({
            type: 'GroupsService:putGroupElement',
            groupElements
        });
        this.log.verbose(JSON.stringify({ GroupElementList: groupElements }));
        return this.http
            .put<IGroupElementResponse>(this.groupElementUri, {
                GroupElementList: groupElements
            })
            .pipe(res => {
                this.log.verbose({
                    type: 'GroupsService:putGroupElement:reponse',
                    res
                });
                return res;
            });
    }
}
